import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { environment } from '@environments/environment';

export interface ChatProductCard {
  skuCode: string;
  name: string;
  salesPrice: number;
  imageUrl: string | null;
  /** Available over all warehouses; null/absent = unknown. */
  availableStock?: number | null;
}

export interface ChatProductCardGroup {
  category: string;
  products: ChatProductCard[];
}

/** A suggested next question from AI Agents; {@code kind} CHEAPER (with {@code price}) or MORE. */
export interface ChatFollowUp {
  kind: string;
  topic: string | null;
  price: number | null;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  products?: ChatProductCard[];
  productGroups?: ChatProductCardGroup[];
  followUps?: ChatFollowUp[];
}

interface ChatApiResponse {
  conversationId: string;
  reply: string;
  products: ChatProductCard[];
  productGroups: ChatProductCardGroup[];
  followUps?: ChatFollowUp[];
}

interface ChatTurnApi {
  userMessage: string;
  assistantReply: string;
  products: ChatProductCard[];
  productGroups: ChatProductCardGroup[];
  followUps?: ChatFollowUp[];
  createdAt: string;
}

interface ChatHistoryPageApi {
  turns: ChatTurnApi[];
  hasMore: boolean;
}

export interface ConversationSummary {
  conversationId: string;
  channel: string;
  lastActivityAt: string;
  /** The conversation's first message, shortened; null when it was blank. */
  title: string | null;
}

/** Turns per history page, both the initial load and each scroll-up "load older" fetch. */
const HISTORY_PAGE_SIZE = 5;

/** Conversations per page of the history list; a shorter page means there are no more. */
const CONVERSATIONS_PAGE_SIZE = 20;

/**
 * State for oms-app's chat widget. Unlike the Ecommerce storefront widget,
 * there is no guest concept here - every caller is an authenticated employee, so the widget
 * always reloads history on open instead of gating that on an auth check, and the BFF proxy has
 * no rate limiting to react to.
 */
@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly http = inject(HttpClient);
  private readonly basePath = environment.aiAgentChatBasePath;

  private readonly openState = signal(false);
  private readonly messagesState = signal<ChatMessage[]>([]);
  private readonly sendingState = signal(false);
  private readonly errorState = signal(false);
  private readonly historyLoadingState = signal(false);
  private readonly hasMoreHistoryState = signal(false);
  private readonly loadingOlderState = signal(false);
  private readonly conversationIdState = signal<string | undefined>(undefined);
  private readonly conversationsState = signal<ConversationSummary[]>([]);
  private readonly conversationsLoadingState = signal(false);
  private readonly conversationsErrorState = signal(false);
  private readonly hasMoreConversationsState = signal(false);
  /** The oldest turn loaded so far - the cursor for the next "load older" page. */
  private oldestTurnCreatedAt: string | undefined;
  private historyRequested = false;

  readonly open = this.openState.asReadonly();
  readonly messages = this.messagesState.asReadonly();
  readonly sending = this.sendingState.asReadonly();
  readonly error = this.errorState.asReadonly();
  readonly hasMessages = computed(() => this.messagesState().length > 0);
  readonly historyLoading = this.historyLoadingState.asReadonly();
  readonly hasMoreHistory = this.hasMoreHistoryState.asReadonly();
  readonly loadingOlder = this.loadingOlderState.asReadonly();
  /** The conversation the widget is showing (and the next send() continues); undefined for a new one. */
  readonly conversationId = this.conversationIdState.asReadonly();
  readonly conversations = this.conversationsState.asReadonly();
  readonly conversationsLoading = this.conversationsLoadingState.asReadonly();
  readonly conversationsError = this.conversationsErrorState.asReadonly();
  readonly hasMoreConversations = this.hasMoreConversationsState.asReadonly();
  /** Starting or switching conversations is refused while this is true, since a late response
   * would write the previous conversation back. */
  readonly busy = computed(() => this.sendingState() || this.historyLoadingState() || this.loadingOlderState());

  toggle(): void {
    const next = !this.openState();
    this.openState.set(next);
    if (next && !this.historyRequested) {
      this.historyRequested = true;
      this.loadHistory();
    }
  }

  close(): void {
    this.openState.set(false);
  }

  /** {@code intent}: only for a suggested follow-up chip - see ChatSuggestion. */
  send(text: string, intent?: string): void {
    const message = text.trim();
    if (!message || this.sendingState()) {
      return;
    }
    this.messagesState.update((messages) => [...messages, { role: 'user', text: message }]);
    this.sendingState.set(true);
    this.errorState.set(false);

    this.http
      .post<ChatApiResponse>(`${this.basePath}/chat`, {
        message,
        conversationId: this.conversationIdState(),
        ...(intent ? { intent } : {}),
      })
      .pipe(
        tap((response) => {
          this.conversationIdState.set(response.conversationId);
          this.messagesState.update((messages) => [
            ...messages,
            {
              role: 'assistant',
              text: response.reply ?? '',
              products: response.products,
              productGroups: response.productGroups,
              ...(response.followUps?.length ? { followUps: response.followUps } : {}),
            },
          ]);
        }),
        catchError(() => {
          this.errorState.set(true);
          return of(null);
        }),
        finalize(() => this.sendingState.set(false)),
      )
      .subscribe();
  }

  /** Clears the widget only - the next send() carries no conversationId, so AI Agents opens a fresh
   * conversation and earlier turns no longer feed into its search. Old turns stay stored. Refused
   * while any request is in flight, since its late response would write the old conversation back. */
  startNewConversation(): void {
    if (this.busy()) {
      return;
    }
    this.conversationIdState.set(undefined);
    this.resetMessages();
  }

  /** Switches the widget to one of the employee's own past conversations; the next send()
   * continues it. Refused while any request is in flight, same as startNewConversation(). */
  openConversation(conversationId: string): void {
    if (this.busy()) {
      return;
    }
    this.reopen(conversationId);
  }

  /** First page of the history list, or (more = true) the page after the last one loaded. Pages
   * by the last row's lastActivityAt + conversationId, so a page already shown is never reset. */
  loadConversations(more = false): void {
    const last = this.conversationsState().at(-1);
    if (this.conversationsLoadingState() || (more && !last)) {
      return;
    }
    this.conversationsLoadingState.set(true);
    this.conversationsErrorState.set(false);
    if (!more) {
      this.conversationsState.set([]);
    }
    let params = new HttpParams().set('limit', CONVERSATIONS_PAGE_SIZE);
    if (more && last) {
      params = params.set('beforeActivityAt', last.lastActivityAt).set('beforeConversationId', last.conversationId);
    }
    this.http
      .get<ConversationSummary[]>(`${this.basePath}/chat/conversations`, { params })
      .pipe(finalize(() => this.conversationsLoadingState.set(false)))
      .subscribe({
        next: (page) => {
          this.conversationsState.update((list) => (more ? [...list, ...page] : page));
          this.hasMoreConversationsState.set(page.length === CONVERSATIONS_PAGE_SIZE);
        },
        error: () => this.conversationsErrorState.set(true),
      });
  }

  private resetMessages(): void {
    this.oldestTurnCreatedAt = undefined;
    this.hasMoreHistoryState.set(false);
    this.errorState.set(false);
    this.messagesState.set([]);
  }

  /** Reloads the employee's own most recent conversation - always, since there is no guest state
   * to gate on here (same history after logout/login). */
  private loadHistory(): void {
    this.historyLoadingState.set(true);
    this.http
      .get<ConversationSummary[]>(`${this.basePath}/chat/conversations`, { params: { limit: 1 } })
      .pipe(catchError(() => of([] as ConversationSummary[])))
      .subscribe((summaries) => {
        const latest = summaries[0]?.conversationId;
        if (!latest) {
          this.historyLoadingState.set(false);
          return;
        }
        this.reopen(latest);
      });
  }

  private reopen(conversationId: string): void {
    this.conversationIdState.set(conversationId);
    this.resetMessages();
    this.historyLoadingState.set(true);
    this.fetchPage(conversationId, undefined, 'replace').subscribe({
      complete: () => this.historyLoadingState.set(false),
    });
  }

  /**
   * Fetches the next page of older turns and prepends them. No-ops (returns a completed
   * Observable immediately) when there's nothing to reopen, no older page left, or a load is
   * already in flight - callers can always just subscribe without checking first.
   */
  loadOlder(): Observable<void> {
    const conversationId = this.conversationIdState();
    if (!conversationId || !this.hasMoreHistoryState() || this.loadingOlderState()) {
      return of(undefined);
    }
    this.loadingOlderState.set(true);
    return this.fetchPage(conversationId, this.oldestTurnCreatedAt, 'prepend').pipe(
      finalize(() => this.loadingOlderState.set(false)),
      map(() => undefined),
    );
  }

  private fetchPage(
    conversationId: string,
    before: string | undefined,
    mode: 'replace' | 'prepend',
  ): Observable<ChatHistoryPageApi> {
    let params = new HttpParams().set('limit', HISTORY_PAGE_SIZE);
    if (before) {
      params = params.set('before', before);
    }
    return this.http
      .get<ChatHistoryPageApi>(`${this.basePath}/chat/conversations/${conversationId}`, { params })
      .pipe(
        tap((page) => this.applyPage(page, mode)),
        catchError(() =>
          of({ turns: [], hasMore: this.hasMoreHistoryState() } as ChatHistoryPageApi),
        ),
      );
  }

  private applyPage(page: ChatHistoryPageApi, mode: 'replace' | 'prepend'): void {
    const turns = page.turns ?? [];
    this.hasMoreHistoryState.set(page.hasMore ?? false);
    if (turns.length > 0) {
      this.oldestTurnCreatedAt = turns[0].createdAt;
    }
    const mapped = turns.flatMap(toMessages);
    this.messagesState.update((messages) => (mode === 'replace' ? mapped : [...mapped, ...messages]));
  }
}

function toMessages(turn: ChatTurnApi): ChatMessage[] {
  return [
    { role: 'user', text: turn.userMessage ?? '' },
    {
      role: 'assistant',
      text: turn.assistantReply ?? '',
      products: turn.products,
      productGroups: turn.productGroups,
      ...(turn.followUps?.length ? { followUps: turn.followUps } : {}),
    },
  ];
}
